import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export interface Column<T> {
  key: keyof T | string;
  label: string;
  sortable?: boolean;
  render?: (row: T) => React.ReactNode;
}

interface ServerTableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading: boolean;
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onSort?: (column: string, direction: "asc" | "desc") => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export function AppTable<T>({
  columns,
  data,
  loading,
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onSort,
  sortBy,
  sortOrder,
}: ServerTableProps<T>) {
  const totalPages = Math.ceil(total / pageSize);

  const handleSort = (key: string) => {
    if (!onSort) return;
    const direction = sortBy === key && sortOrder === "asc" ? "desc" : "asc";
    onSort(key, direction);
  };

  return (
    <div className="bg-white rounded-xl shadow overflow-hidden">

      {/* Table */}
      <table className="w-full border-collapse">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((col) => (
              <th
                key={String(col.key)}
                className={`p-3 text-left font-semibold ${
                  col.sortable ? "cursor-pointer hover:text-blue-500" : ""
                }`}
                onClick={() => col.sortable && handleSort(String(col.key))}
              >
                <div className="flex items-center gap-1">
                  {col.label}

                  {col.sortable && sortBy === col.key && (
                    <span>{sortOrder === "asc" ? "▲" : "▼"}</span>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {/* Loading */}
          {loading && (
            <tr>
              <td colSpan={columns.length} className="text-center py-6 text-gray-500">
                Loading...
              </td>
            </tr>
          )}

          {/* Empty */}
          {!loading && data.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="text-center py-6 text-gray-400">
                No records found.
              </td>
            </tr>
          )}

          {/* Rows */}
          {!loading &&
            data.map((row, idx) => (
              <tr key={idx} className="border-t hover:bg-gray-50">
                {columns.map((col) => (
                  <td key={String(col.key)} className="p-3">
                    {col.render ? col.render(row) : (row as any)[col.key]}
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>

      {/* Pagination */}
      <div className="flex justify-between items-center p-3 border-t bg-gray-50">

        {/* Page size */}
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="border px-2 py-1 rounded"
        >
          {[5, 10, 20, 50].map((size) => (
            <option key={size} value={size}>
              {size} / page
            </option>
          ))}
        </select>

        {/* Pagination Controls */}
        <div className="flex items-center gap-2">
          <button
            disabled={page === 1}
            onClick={() => onPageChange(page - 1)}
            className="p-1 border rounded disabled:opacity-40"
          >
            <ChevronLeft size={18} />
          </button>

          <span className="text-sm">
            Page {page} of {totalPages}
          </span>

          <button
            disabled={page === totalPages}
            onClick={() => onPageChange(page + 1)}
            className="p-1 border rounded disabled:opacity-40"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
